import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// --- utils ---
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function parseFraction(raw) {
  const s = String(raw).replace(",", ".").trim();
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s,10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (!m || parseInt(m[2],10) === 0) return null;
  return [parseInt(m[1],10), parseInt(m[2],10)];
}

// odmiana słowa "los"
function losWord(n) {
  if (n === 1) return "los";
  if (n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20)) return "losy";
  return "losów";
}

function wygrywajacyForm(n) {
  if (n === 1) return "wygrywający";
  const twoToFour = (n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20));
  return twoToFour ? "wygrywające" : "wygrywających";
}
function przegrywajacyForm(n) {
  if (n === 1) return "przegrywający";
  const twoToFour = (n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20));
  return twoToFour ? "przegrywające" : "przegrywających";
}

function generateOne() {
  const N = 50 + Math.floor(Math.random() * 41); // 50–90
  const K = 5 + Math.floor(Math.random() * 11);  // 5–15
  const L = N - K;                                // przegrywające

  const m = 3 + Math.floor(Math.random() * 8);    // 3–10 kupionych

  const minW = Math.max(1, m - L);
  const maxW = Math.min(m - 1, K);
  const w = minW + Math.floor(Math.random() * (Math.max(0, maxW - minW + 1)));
  const l = m - w;

  const Np = N - m;
  const Kp = K - w;
  const [p, q] = simplify(Kp, Np);

  return { N, K, L, m, w, l, Np, Kp, p, q }; // czyste dane
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "LotteryOneDrawTask",
    data: d,
    uid: `lottery1_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      W loterii znajduje się <InlineMath math={`${data.N}`} /> {losWord(data.N)}, z czego{" "}
      <InlineMath math={`${data.K}`} /> wygrywających.
      <br />
      Przed Tobą ktoś kupił <InlineMath math={`${data.m}`} /> {losWord(data.m)}, w tym{" "}
      <InlineMath math={`${data.w}`} /> {wygrywajacyForm(data.w)} i{" "}
      <InlineMath math={`${data.l}`} /> {przegrywajacyForm(data.l)}.
      <br />
      Oblicz prawdopodobieństwo, że wylosowany przez Ciebie los okaże się wygrywający.
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 7/83"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 7/83) lub liczbę." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);
  const correct = (gpp === cp && gqq === cq);
  return { correct, expected: `\\tfrac{${cp}}{${cq}}`, needsSimplification: !correct && (gp/gq === cp/cq) };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { N, K, m, w, Np, Kp } = data;
  const [simpNum, simpDen] = simplify(Kp, Np);
  const tail = (simpNum === Kp && simpDen === Np) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const omegaCalc = `${N} - ${m} = ${Np}`;
  const aCalc     = `${K} - ${w} = ${Kp}`;
  const probLine  = `P(\\mathrm{A}) = \\dfrac{${Kp}}{${Np}} ${tail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <div>
          Po zakupie <InlineMath math={`${m}`} /> {losWord(m)} pozostało{" "}
          <InlineMath math={`${Np}`} /> {losWord(Np)}.
        </div>
        <BlockMath math={omegaCalc} />
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — zdarzenie A</strong>
        <div>
          Z początkowych <InlineMath math={`${K}`} /> wygrywających odjęto{" "}
          <InlineMath math={`${w}`} /> {wygrywajacyForm(w)} — zostało{" "}
          <InlineMath math={`${Kp}`} /> {wygrywajacyForm(Kp)}.
        </div>
        <BlockMath math={aCalc} />
      </div>

      <div>
        <strong>Krok 3 — prawdopodobieństwo</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}
